import { collection, onSnapshot, query } from "firebase/firestore";
import { useEffect, useRef } from "react";
import toast from "react-hot-toast";
import { db } from "src/firebase";
import { UserReport } from "src/features/users-reports/types/user-report.entity";
import { getGlobalNotificationsEnabled, setUsersReportsHasUnreadAlert } from "src/utils/browserStorage.util";

const USER_REPORTS_COLLECTION = "reports";
const ALERT_LOCK_PREFIX = "user-reports-alert-lock";
const ALERT_LOCK_TTL_MS = 15000;

export const UserReportsNotification = () => {
  const hasInitializedRef = useRef(false);
  const audioContextRef = useRef<AudioContext | null>(null);
  const soundUnlockedRef = useRef(false);
  const speechUnlockedRef = useRef(false);
  const tabIdRef = useRef(`user-reports-tab-${Math.random().toString(36).slice(2)}`);

  useEffect(() => {
    if (typeof window === "undefined") return;

    const unlockAlerts = async () => {
      if (!audioContextRef.current) {
        const AudioContextClass =
          window.AudioContext || (window as typeof window & { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
        if (AudioContextClass) {
          audioContextRef.current = new AudioContextClass();
        }
      }

      if (audioContextRef.current?.state === "suspended") {
        await audioContextRef.current.resume();
      }

      soundUnlockedRef.current = audioContextRef.current?.state === "running";

      if ("Notification" in window && Notification.permission === "default") {
        Notification.requestPermission().catch(() => undefined);
      }

      if ("speechSynthesis" in window) {
        window.speechSynthesis.getVoices();
        speechUnlockedRef.current = true;
      }
    };

    const handleUserInteraction = () => {
      unlockAlerts().catch(() => undefined);
    };

    window.addEventListener("pointerdown", handleUserInteraction);
    window.addEventListener("keydown", handleUserInteraction);

    return () => {
      window.removeEventListener("pointerdown", handleUserInteraction);
      window.removeEventListener("keydown", handleUserInteraction);
    };
  }, []);

  const playFallbackSound = () => {
    const audioContext = audioContextRef.current;
    if (!audioContext || !soundUnlockedRef.current) return;

    const now = audioContext.currentTime;
    const oscillator = audioContext.createOscillator();
    const gainNode = audioContext.createGain();

    oscillator.type = "sine";
    oscillator.frequency.setValueAtTime(784, now);
    oscillator.frequency.exponentialRampToValueAtTime(1046, now + 0.18);

    gainNode.gain.setValueAtTime(0.0001, now);
    gainNode.gain.exponentialRampToValueAtTime(0.08, now + 0.02);
    gainNode.gain.exponentialRampToValueAtTime(0.0001, now + 0.35);

    oscillator.connect(gainNode);
    gainNode.connect(audioContext.destination);

    oscillator.start(now);
    oscillator.stop(now + 0.36);
  };

  const speakUserReport = () => {
    if (typeof window === "undefined" || !("speechSynthesis" in window)) return false;
    if (!speechUnlockedRef.current) return false;

    const utterance = new SpeechSynthesisUtterance("user report");
    const voices = window.speechSynthesis.getVoices();
    const preferredVoice =
      voices.find((voice) => /en/i.test(voice.lang) && /female|zira|aria|susan|samantha|google us english/i.test(voice.name)) ||
      voices.find((voice) => /en/i.test(voice.lang)) ||
      voices[0];

    if (preferredVoice) {
      utterance.voice = preferredVoice;
      utterance.lang = preferredVoice.lang;
    } else {
      utterance.lang = "en-US";
    }

    utterance.rate = 1;
    utterance.pitch = 1;
    utterance.volume = 1;

    window.speechSynthesis.cancel();
    window.speechSynthesis.speak(utterance);
    return true;
  };

  const showBrowserNotification = (report: UserReport & { id: string }) => {
    if (typeof window === "undefined" || !("Notification" in window)) return;
    if (Notification.permission === "default") {
      Notification.requestPermission().catch(() => undefined);
      return;
    }
    if (Notification.permission !== "granted") return;

    new Notification("New user report", {
      body: report.reason || report.reportedFrom || report.reportedId || "A new user report was submitted",
      icon: "/taaly-logo.png",
      tag: `user-report-${report.id}`,
    });
  };

  const tryClaimNotification = (notificationId: string) => {
    if (typeof window === "undefined") return true;

    const lockKey = `${ALERT_LOCK_PREFIX}:${notificationId}`;
    const lockValue = {
      owner: tabIdRef.current,
      expiresAt: Date.now() + ALERT_LOCK_TTL_MS,
    };

    try {
      const rawExistingLock = window.localStorage.getItem(lockKey);
      if (rawExistingLock) {
        const existingLock = JSON.parse(rawExistingLock) as { owner?: string; expiresAt?: number };
        if (existingLock.expiresAt && existingLock.expiresAt > Date.now()) {
          return existingLock.owner === tabIdRef.current;
        }
      }

      window.localStorage.setItem(lockKey, JSON.stringify(lockValue));
      const rawConfirmedLock = window.localStorage.getItem(lockKey);
      if (!rawConfirmedLock) return false;

      const confirmedLock = JSON.parse(rawConfirmedLock) as { owner?: string; expiresAt?: number };
      return confirmedLock.owner === tabIdRef.current;
    } catch {
      return true;
    }
  };

  useEffect(() => {
    const userReportsQuery = query(collection(db, USER_REPORTS_COLLECTION));

    const unsubscribe = onSnapshot(userReportsQuery, (snapshot) => {
      if (!hasInitializedRef.current) {
        hasInitializedRef.current = true;
        return;
      }

      snapshot.docChanges().forEach((change) => {
        if (change.type !== "added") {
          return;
        }

        if (!getGlobalNotificationsEnabled()) {
          return;
        }

        const report = { id: change.doc.id, ...change.doc.data() } as UserReport & { id: string };
        setUsersReportsHasUnreadAlert(true);

        if (!tryClaimNotification(report.id)) {
          return;
        }

        const didSpeak = speakUserReport();
        if (!didSpeak) {
          playFallbackSound();
        }

        showBrowserNotification(report);
        toast.success(`New user report: ${report.reason || report.reportedFrom || report.reportedId || report.id}`, {
          id: `user-report-${report.id}`,
        });
      });
    });

    return () => unsubscribe();
  }, []);

  return null;
};
